import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { getCmsNavigation } from '../lib/cms';

export default function Header() {
  const [navItems, setNavItems] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let isMounted = true;
    getCmsNavigation().then(items => {
      if (isMounted && items && items.length > 0) {
        setNavItems(items);
      }
    });
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className="sticky top-0 z-50 w-full transition-all duration-200">
      {/* Top Utility Bar with Contact Info */}
      <div className="bg-[#041c2c] text-slate-300 border-b border-slate-800/80 text-xs py-2 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-y-1 gap-x-4">
          <div className="flex items-center gap-2 text-slate-400">
            <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
            <span className="font-medium text-slate-200">Døgnvagt 24/7/365</span>
            <span className="hidden sm:inline text-slate-500">•</span>
            <span className="hidden sm:inline text-slate-400">Jylland & Fyn</span>
          </div>

          <div className="flex items-center gap-4 sm:gap-6 ml-auto whitespace-nowrap">
            <a
              href="tel:+4523964042"
              className="inline-flex items-center gap-1.5 text-slate-200 hover:text-[#b39366] transition-colors font-medium whitespace-nowrap"
              title="Ring til Akut Vikaren ApS"
            >
              <i className="fa-solid fa-phone text-[#b39366] text-xs" aria-hidden="true"></i>
              <span className="whitespace-nowrap">(+45) 23 96 40 42</span>
            </a>

            <a
              href="mailto:kontakt@akutvikaren.dk"
              className="inline-flex items-center gap-1.5 text-slate-200 hover:text-[#b39366] transition-colors whitespace-nowrap"
              title="Send en e-mail til Akut Vikaren ApS"
            >
              <i className="fa-solid fa-envelope text-[#b39366] text-xs" aria-hidden="true"></i>
              <span className="whitespace-nowrap">kontakt@akutvikaren.dk</span>
            </a>

            {/* Top Bar Socials */}
            <div className="hidden sm:flex items-center gap-2 pl-2 border-l border-slate-700">
              <a
                href="https://www.facebook.com/Akutvikaren"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Akut Vikaren Facebook"
                className="text-slate-400 hover:text-[#b39366] transition-colors p-1"
              >
                <i className="fa-brands fa-facebook-f text-xs" aria-hidden="true"></i>
              </a>
              <a
                href="https://www.instagram.com/akutvikaren"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Akut Vikaren Instagram"
                className="text-slate-400 hover:text-[#b39366] transition-colors p-1"
              >
                <i className="fa-brands fa-instagram text-xs" aria-hidden="true"></i>
              </a>
              <a
                href="https://www.linkedin.com/company/akut-vikaren-aps/?originalSubdomain=dk"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Akut Vikaren LinkedIn"
                className="text-slate-400 hover:text-[#b39366] transition-colors p-1"
              >
                <i className="fa-brands fa-linkedin-in text-xs" aria-hidden="true"></i>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Navigation Bar */}
      <div
        className={`w-full bg-[#041c2c] border-b border-slate-800 transition-all duration-200 ${
          scrolled ? 'shadow-xl py-2.5' : 'py-3.5'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 group focus:outline-none focus-visible:ring-2 focus-visible:ring-[#b39366] rounded"
          >
            <img
              src="https://assets.auxodev.dk/assets/001da5eb-368a-49cd-a912-65694d60b551/cropped-image0-removebg-preview.png"
              alt="Akut Vikaren ApS Logo"
              className="h-10 sm:h-12 w-auto object-contain transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Desktop Navigation (Visible >= 1200px) */}
          <nav className="hidden min-[1200px]:flex items-center space-x-1 lg:space-x-2" aria-label="Hovednavigation">
            {navItems.map((item) => (
              <NavLink
                key={item.id || item.path}
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) =>
                  `px-3.5 py-2 text-sm font-medium transition-all duration-150 relative ${
                    isActive
                      ? 'text-[#b39366] font-semibold after:absolute after:bottom-0 after:left-3.5 after:right-3.5 after:h-0.5 after:bg-[#b39366]'
                      : 'text-slate-200 hover:text-white hover:bg-slate-800/60 rounded-md'
                  }`
                }
              >
                {item.title}
              </NavLink>
            ))}

            {/* Direct CTA Button */}
            <a
              href="tel:+4523964042"
              className="ml-3 inline-flex items-center gap-2 bg-[#b39366] hover:bg-[#a18155] text-white text-sm font-semibold px-4 py-2 rounded shadow-sm hover:shadow transition-all duration-150 whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
              <span className="whitespace-nowrap">Akut kontakt</span>
            </a>
          </nav>

          {/* Burger Menu Button (Visible < 1200px) */}
          <div className="flex items-center gap-2 min-[1200px]:hidden">
            <a
              href="tel:+4523964042"
              className="inline-flex items-center gap-1.5 bg-[#b39366] hover:bg-[#a18155] text-white text-xs font-semibold px-3 py-1.5 rounded transition-colors whitespace-nowrap"
            >
              <i className="fa-solid fa-phone text-[11px]" aria-hidden="true"></i>
              <span>Ring op</span>
            </a>

            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 text-slate-300 hover:text-white hover:bg-slate-800 rounded focus:outline-none focus:ring-2 focus:ring-[#b39366]"
              aria-expanded={mobileMenuOpen}
              aria-label={mobileMenuOpen ? 'Luk navigation' : 'Åbn navigation'}
            >
              {mobileMenuOpen ? (
                <i className="fa-solid fa-xmark text-2xl w-6 h-6 flex items-center justify-center" aria-hidden="true"></i>
              ) : (
                <i className="fa-solid fa-bars text-2xl w-6 h-6 flex items-center justify-center" aria-hidden="true"></i>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile / Tablet Drawer (< 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-[#041c2c] border-b border-slate-800 shadow-2xl animate-in slide-in-from-top-2 duration-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 space-y-3">
            <nav className="flex flex-col space-y-1.5" aria-label="Mobil navigation">
              {navItems.map((item) => (
                <NavLink
                  key={item.id || item.path}
                  to={item.path}
                  end={item.path === '/'}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `px-4 py-3 text-base font-medium rounded-lg transition-all ${
                      isActive
                        ? 'bg-slate-800/90 text-[#b39366] font-semibold border-l-4 border-[#b39366]'
                        : 'text-slate-200 hover:text-white hover:bg-slate-800/60'
                    }`
                  }
                >
                  {item.title}
                </NavLink>
              ))}
            </nav>

            <div className="pt-4 mt-4 border-t border-slate-800/80 space-y-3">
              <div className="bg-slate-900/80 p-4 rounded-lg border border-slate-800 space-y-2">
                <p className="text-xs font-semibold text-[#b39366] uppercase tracking-wider">
                  Døgnåben kontakt 24/7
                </p>
                <div className="flex flex-col gap-2 pt-1">
                  <a
                    href="tel:+4523964042"
                    className="flex items-center gap-2.5 text-white hover:text-[#b39366] text-sm font-semibold whitespace-nowrap"
                  >
                    <div className="w-7 h-7 rounded-full bg-[#b39366]/20 flex items-center justify-center text-[#b39366]">
                      <i className="fa-solid fa-phone text-xs" aria-hidden="true"></i>
                    </div>
                    <span className="whitespace-nowrap">(+45) 23 96 40 42</span>
                  </a>

                  <a
                    href="mailto:kontakt@akutvikaren.dk"
                    className="flex items-center gap-2.5 text-slate-300 hover:text-[#b39366] text-sm whitespace-nowrap"
                  >
                    <div className="w-7 h-7 rounded-full bg-[#b39366]/20 flex items-center justify-center text-[#b39366]">
                      <i className="fa-solid fa-envelope text-xs" aria-hidden="true"></i>
                    </div>
                    <span className="whitespace-nowrap">kontakt@akutvikaren.dk</span>
                  </a>
                </div>
              </div>

              {/* Mobile Socials */}
              <div className="flex items-center justify-center gap-4 py-2 border-y border-slate-800">
                <span className="text-xs text-slate-400 font-medium">Følg os:</span>
                <a
                  href="https://www.facebook.com/Akutvikaren"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Facebook"
                  className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-slate-300 hover:text-white hover:bg-[#b39366] transition-colors"
                >
                  <i className="fa-brands fa-facebook-f text-xs" aria-hidden="true"></i>
                </a>
                <a
                  href="https://www.instagram.com/akutvikaren"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram"
                  className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-slate-300 hover:text-white hover:bg-[#b39366] transition-colors"
                >
                  <i className="fa-brands fa-instagram text-xs" aria-hidden="true"></i>
                </a>
                <a
                  href="https://www.linkedin.com/company/akut-vikaren-aps/?originalSubdomain=dk"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn"
                  className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-slate-300 hover:text-white hover:bg-[#b39366] transition-colors"
                >
                  <i className="fa-brands fa-linkedin-in text-xs" aria-hidden="true"></i>
                </a>
              </div>

              <a
                href="tel:+4523964042"
                className="w-full flex items-center justify-center gap-2 bg-[#b39366] hover:bg-[#a18155] text-white font-semibold py-3 px-4 rounded shadow transition-colors"
              >
                <i className="fa-solid fa-phone" aria-hidden="true"></i>
                <span>Ring nu (+45) 23 96 40 42</span>
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
